// Recurso.configuracoes.iteracao = { comandoLoop, campos }
//  - comandoLoop: apesar do nome, é o id do RECURSO filho (o que se repete para cada item da lista)
//  - campos: campo do registro da listagem -> nome da variável usada como {{variavel}} no path do endpoint do filho

export type IteracaoCampoForm = { campo: string; variavel: string }

export type IteracaoFilhoForm = {
  // presente quando o filho já existe (edição)
  id?: number;
  nome: string;
  descricao: string;
}

export type IteracaoForm = {
  campos: IteracaoCampoForm[];
  filho: IteracaoFilhoForm;
  // Comando do filho (API): conexaoId, endpointId, comando (corpo), parametros, camposChave...
  comandoFilho: any;
}

const NOME_VARIAVEL = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function novoComandoFilho(): any {
  return { nome: 'Detalhe do item', descricao: '', tipo: 'API_ENDPOINT', conexaoId: undefined, endpointId: undefined, comando: '', parametros: {}, camposChave: [] };
}

export function novoIteracaoForm(): IteracaoForm {
  return { campos: [{ campo: '', variavel: '' }], filho: { nome: '', descricao: '' }, comandoFilho: novoComandoFilho() };
}

/** Lê Recurso.configuracoes.iteracao.campos (mapa) para as linhas do formulário */
export function mapaParaCampos(mapa: Record<string, string> | null | undefined): IteracaoCampoForm[] {
  const linhas = Object.entries(mapa ?? {}).map(([campo, variavel]) => ({ campo, variavel: String(variavel) }));
  return linhas.length > 0 ? linhas : [{ campo: '', variavel: '' }];
}

/** Monta o mapa gravado em configuracoes.iteracao.campos */
export function camposParaMapa(campos: IteracaoCampoForm[]): Record<string, string> {
  const mapa: Record<string, string> = {};
  for (const c of campos) mapa[c.campo.trim()] = c.variavel.trim();
  return mapa;
}

/** Variáveis declaradas ({{nome}} nos paths do filho) */
export function nomesDeVariaveis(campos: IteracaoCampoForm[]): string[] {
  return campos.map(c => c.variavel.trim()).filter(v => v.length > 0);
}

/** Variáveis declaradas que o path do endpoint do filho não usa como {{variavel}} */
export function variaveisNaoUsadas(path: string | null | undefined, variaveis: string[]): string[] {
  const p = path ?? '';
  return variaveis.filter(v => !new RegExp(`\\{\\{\\s*${v}\\s*\\}\\}`).test(p));
}

/** Texto do exemplo: o que cada item da listagem vira no filho */
export function previewIteracao(campos: IteracaoCampoForm[]): string[] {
  return campos
    .filter(c => c.campo.trim() && c.variavel.trim())
    .map(c => `Para cada item da lista, {{${c.variavel.trim()}}} recebe o valor do campo "${c.campo.trim()}".`);
}

/** Primeiro problema da parte do formulário que é específica da iteração (campos e recurso por item) */
export function validarIteracao(form: IteracaoForm): string | null {
  if (form.campos.length === 0) return 'Informe ao menos um campo da listagem que vira variável do recurso por item.';
  const usadas = new Set<string>();
  const camposUsados = new Set<string>();
  for (const [i, c] of form.campos.entries()) {
    if (!c.campo.trim() || !c.variavel.trim()) return `Campo ${i + 1}: informe o campo da listagem e o nome da variável.`;
    if (!NOME_VARIAVEL.test(c.variavel.trim())) return `Campo ${i + 1}: o nome da variável "${c.variavel.trim()}" deve ter só letras, números e "_", sem começar por número.`;
    if (usadas.has(c.variavel.trim().toLowerCase())) return `A variável "${c.variavel.trim()}" aparece mais de uma vez.`;
    if (camposUsados.has(c.campo.trim())) return `O campo "${c.campo.trim()}" aparece mais de uma vez.`;
    usadas.add(c.variavel.trim().toLowerCase());
    camposUsados.add(c.campo.trim());
  }
  if (!form.filho.nome.trim()) return 'Informe o nome do recurso executado por item.';
  const c = form.comandoFilho;
  if (!c?.nome?.trim()) return 'Informe o nome do comando do recurso por item.';
  if (!c.endpointId) return 'Selecione o endpoint do recurso por item.';
  return null;
}
